import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ClockCircleOutlined } from '@ant-design/icons'
import '../../styles/aurora-page.css'
import '../../styles/aurora-modal.css'
import * as timersApi from '@/api/timers'
import type { TimerSchedule, TimerTargetOption } from '@/types'
import { useProjectStore } from '@/stores/project'

const { Title, Text } = Typography

/** 常用 cron 快捷预设（5 段：分 时 日 月 周） */
const CRON_PRESETS = [
  { value: '0 8 * * *', label: '每天 08:00' },
  { value: '0 9 * * 1-5', label: '工作日 09:00' },
  { value: '0 12 * * *', label: '每天 12:00' },
  { value: '0 20 * * *', label: '每天 20:00' },
  { value: '0 */2 * * *', label: '每 2 小时' },
  { value: '*/30 * * * *', label: '每 30 分钟' },
]

const TYPE_META: Record<string, { label: string; color: string }> = {
  scenario: { label: '接口场景', color: 'blue' },
  ui_script: { label: 'UI 用例', color: 'purple' },
}

/**
 * 通用定时器管理页（测试计划 → 定时器）
 * - 定时计划支持接口场景用例与 UI 自动化用例，cron 5 段表达式
 * - 场景由后端执行器直接执行，UI 用例投递 Playwright 队列，执行记录统一在「定时器执行记录」页查看
 */
export default function Timers() {
  const navigate = useNavigate()
  const { currentProject } = useProjectStore()
  const projectId = currentProject?.id || 0

  const [schedules, setSchedules] = useState<TimerSchedule[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const [targets, setTargets] = useState<{ scenario: TimerTargetOption[]; ui_script: TimerTargetOption[] }>({
    scenario: [],
    ui_script: [],
  })
  const [editOpen, setEditOpen] = useState(false)
  const [editing, setEditing] = useState<TimerSchedule | null>(null)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm()

  const targetType = Form.useWatch('target_type', form)

  const loadSchedules = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const res = await timersApi.listSchedules({ project_id: projectId, page, page_size: pageSize })
      setSchedules(res.data)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [projectId, page, pageSize])

  useEffect(() => {
    loadSchedules()
  }, [loadSchedules])

  // 目标下拉数据源（接口场景 + UI 用例）
  useEffect(() => {
    if (!projectId) return
    timersApi
      .listTargets(projectId)
      .then(setTargets)
      .catch(() => setTargets({ scenario: [], ui_script: [] }))
  }, [projectId])

  const openCreate = () => {
    setEditing(null)
    form.setFieldsValue({
      target_type: 'scenario',
      target_id: undefined,
      name: '',
      cron_expr: '0 8 * * *',
      enabled: true,
    })
    setEditOpen(true)
  }

  const openEdit = (record: TimerSchedule) => {
    setEditing(record)
    form.setFieldsValue({
      target_type: record.target_type,
      target_id: record.target_id,
      name: record.name,
      cron_expr: record.cron_expr,
      enabled: record.enabled,
    })
    setEditOpen(true)
  }

  const handleSave = async () => {
    const values = await form.validateFields()
    setSaving(true)
    try {
      if (editing) {
        const res = await timersApi.updateSchedule(editing.id, projectId, {
          name: values.name,
          cron_expr: values.cron_expr,
          enabled: values.enabled,
        })
        message.success(
          `计划已更新${res?.next_run_time ? `，下次执行 ${new Date(res.next_run_time).toLocaleString('zh-CN', { hour12: false })}` : ''}`
        )
      } else {
        const res = await timersApi.createSchedule({
          project_id: projectId,
          target_type: values.target_type,
          target_id: values.target_id,
          name: values.name,
          cron_expr: values.cron_expr,
          enabled: values.enabled,
        })
        message.success(
          `定时计划已创建${res?.next_run_time ? `，下次执行 ${new Date(res.next_run_time).toLocaleString('zh-CN', { hour12: false })}` : ''}`
        )
      }
      setEditOpen(false)
      loadSchedules()
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = async (record: TimerSchedule, enabled: boolean) => {
    await timersApi.setScheduleEnabled(record.id, projectId, enabled)
    message.success(enabled ? '计划已启用' : '计划已停用')
    loadSchedules()
  }

  const handleDelete = async (id: number) => {
    await timersApi.deleteSchedule(id, projectId)
    message.success('定时计划已删除')
    loadSchedules()
  }

  const columns: ColumnsType<TimerSchedule> = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '计划名称', dataIndex: 'name', width: 200, ellipsis: true },
    {
      title: '类型',
      dataIndex: 'target_type',
      width: 110,
      render: (v: string) => <Tag color={TYPE_META[v]?.color || 'default'}>{TYPE_META[v]?.label || v}</Tag>,
    },
    { title: '执行目标', dataIndex: 'target_name', width: 180, ellipsis: true },
    {
      title: 'cron 表达式',
      dataIndex: 'cron_expr',
      width: 150,
      render: (v: string) => (
        <Text code style={{ fontSize: 12 }}>{v}</Text>
      ),
    },
    {
      title: '启用',
      dataIndex: 'enabled',
      width: 90,
      render: (v: boolean, r) => <Switch size="small" checked={v} onChange={(e) => handleToggle(r, e)} />,
    },
    {
      title: '下次执行',
      dataIndex: 'next_run_time',
      width: 170,
      render: (v: string | null, r) =>
        v ? (
          <span style={{ fontSize: 13 }}>{new Date(v).toLocaleString('zh-CN', { hour12: false })}</span>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>{r.enabled ? '-' : '已停用'}</Text>
        ),
    },
    {
      title: '上次执行',
      dataIndex: 'last_run_time',
      width: 170,
      render: (v: string | null) =>
        v ? (
          <span style={{ fontSize: 13 }}>{new Date(v).toLocaleString('zh-CN', { hour12: false })}</span>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>未执行</Text>
        ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_, r) => (
        <Space size={4}>
          <Button type="link" size="small" style={{ padding: '0 4px' }} onClick={() => openEdit(r)}>编辑</Button>
          <Button
            type="link"
            size="small"
            style={{ padding: '0 4px' }}
            onClick={() => navigate(`/timer-runs?schedule_id=${r.id}`)}
          >
            记录
          </Button>
          <Popconfirm
            title="确定删除该定时计划？"
            onConfirm={() => handleDelete(r.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" size="small" danger style={{ padding: '0 4px' }}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="aurora-page aurora-page--fit">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Title level={4} style={{ margin: 0, fontWeight: 600 }}>定时器</Title>
          <Tag color="geekblue">接口场景 + UI 用例</Tag>
          <Tag color="purple">APScheduler</Tag>
        </div>
        <Space>
          <Button onClick={loadSchedules}>刷新</Button>
          <Button type="primary" onClick={openCreate}>新建计划</Button>
        </Space>
      </div>

      <div className="content-card">
        <div className="content-card-header">
          <div className="content-card-title">
            <ClockCircleOutlined style={{ marginRight: 8, color: 'var(--au-primary)' }} />
            定时计划
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            到达 cron 触发时间后自动执行对应用例（无需人工值守），执行结果在「定时器执行记录」页查看
          </Text>
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={schedules}
            size="middle"
            scroll={{ y: '100%' }}
            locale={{ emptyText: '暂无定时计划' }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showTotal: (t) => `共 ${t} 条`,
              onChange: (p, ps) => {
                setPage(p)
                setPageSize(ps)
              },
            }}
          />
        </div>
      </div>

      <Modal
        title={editing ? '编辑定时计划' : '新建定时计划'}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        width={540}
        rootClassName="aurora-modal-wrap"
        className="aurora-modal"
      >
        <Form layout="vertical" form={form} style={{ marginTop: 8 }}>
          <Form.Item name="target_type" label="用例类型" rules={[{ required: true, message: '请选择用例类型' }]}>
            <Select
              disabled={!!editing}
              onChange={() => form.setFieldValue('target_id', undefined)}
              options={[
                { value: 'scenario', label: '接口场景用例' },
                { value: 'ui_script', label: 'UI 自动化用例' },
              ]}
            />
          </Form.Item>
          <Form.Item name="target_id" label="执行用例" rules={[{ required: true, message: '请选择执行用例' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={targetType === 'ui_script' ? '选择 UI 自动化用例' : '选择接口场景用例'}
              disabled={!!editing}
              options={(targets[targetType as keyof typeof targets] || []).map((t) => ({
                value: t.id,
                label: t.name,
              }))}
            />
          </Form.Item>
          <Form.Item name="name" label="计划名称" rules={[{ required: true, message: '请输入计划名称' }]}>
            <Input placeholder="如：核心链路每日回归" maxLength={255} />
          </Form.Item>
          <Form.Item
            name="cron_expr"
            label="cron 表达式（5 段：分 时 日 月 周）"
            rules={[
              { required: true, message: '请输入 cron 表达式' },
              {
                validator: (_, v: string) => {
                  const parts = (v || '').trim().split(/\s+/)
                  return parts.length === 5
                    ? Promise.resolve()
                    : Promise.reject(new Error('必须是 5 段：分 时 日 月 周'))
                },
              },
            ]}
            extra={
              <Space size={4} wrap style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>快捷：</Text>
                {CRON_PRESETS.map((p) => (
                  <Button
                    key={p.value}
                    size="small"
                    type="dashed"
                    style={{ padding: '0 6px', fontSize: 12 }}
                    onClick={() => form.setFieldValue('cron_expr', p.value)}
                  >
                    {p.label}
                  </Button>
                ))}
              </Space>
            }
          >
            <Input placeholder="0 8 * * *" maxLength={64} style={{ fontFamily: 'Consolas, monospace' }} />
          </Form.Item>
          <Form.Item name="enabled" label="状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
